/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { v4 as uuidv4 } from 'uuid';
import { useEffect, useState } from 'react';
import HighchartsReact from 'highcharts-react-official';
import JsonQuery from 'json-query';
import { merge } from 'object-path-immutable';
import parse from 'parse-json';
import _ from 'lodash';
import {
    hooks as commonHooks,
    types as commonTypes,
    localization as commonLocalization,
} from '@sitebuilder/common';
import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';
import ChartTable from '../ChartTable/ChartTable';
import {
    getThemeBasedColorPalette,
    getColorPalette,
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import { sampleData } from './sampleData';
import HoldingsOverTimeChartStyles from './HoldingsOverTimeChart.styles';

interface highChartOptions {
    path?: string;
    value?: string;
}

interface TableColumn {
    label?: string;
    value?: string;
    isOnlyText?: boolean;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    dataKey: string;
    options: highChartOptions[];
    chartTitles: Headings[];
    columns?: TableColumn[];
    otherText: string;
    isLimitHoldingsToTen: boolean;
    isSortHoldingsTable: boolean;
    orderToSortHoldingsTable: string;
    paletteId: string;
    typographyClass?: string;
}

const getPreparedData = (
    data: any,
    otherText: string,
    isLimitHoldingsToTen: boolean,
    isSortHoldingsTable: boolean,
    orderToSortHoldingsTable: string,
    i18n: commonLocalization.II18N
) => {
    const preparedData: any = [];
    let holdingNames: string[] = [];
    const initialLegend: any = [];

    Array.isArray(data) &&
        data.forEach((d: any, index: number) => {
            const allocations = [];
            let a1 = 0;

            for (let i = 0; i < d?.allocations?.length; i++) {
                const a = d.allocations[i];
                const _allocation = { ...a, holding: a?.holding?.trim() };
                const isAddToInitialLegend = index === 0 && _allocation.allocation >= 0;

                if (!isLimitHoldingsToTen) {
                    allocations.push(_allocation);
                    isAddToInitialLegend && initialLegend.push(_allocation);
                    a1 = a1 + parseFloat(a.allocation);
                } else {
                    if (i < 9) {
                        allocations.push(_allocation);
                        isAddToInitialLegend && initialLegend.push(_allocation);
                        a1 = a1 + parseFloat(a.allocation);
                    } else {
                        const other = {
                            holding: otherText,
                            allocation: 1 - a1,
                            effectiveDate: a.effectiveDate,
                        };
                        allocations.push(other);
                        index === 0 && initialLegend.push(other);
                        break;
                    }
                }
            }

            Array.isArray(allocations) &&
                allocations.forEach((a: any) => {
                    const _date = a.effectiveDate;
                    const splitDate = _date.split('-');
                    const year = splitDate[0];
                    const month = splitDate[1] - 1;
                    const day = splitDate[2];
                    const date = Date.UTC(year, month, day);
                    const allocation = parseFloat(`${i18n.t(a.allocation, 'pctMax2')}`);

                    if (holdingNames.includes(a.holding)) {
                        const index = preparedData.findIndex((i: any) => i.name === a.holding);
                        preparedData[index]?.data.push({
                            x: date,
                            y: allocation,
                        });
                    } else {
                        preparedData.push({
                            showInNavigator: true,
                            name: a.holding,
                            data: [{ x: date, y: allocation }],
                        });
                        holdingNames.push(a.holding);
                    }
                });
        });

    if (isSortHoldingsTable && holdingNames?.length) {
        const sortOrder = orderToSortHoldingsTable?.length
            ? orderToSortHoldingsTable.split(',')
            : [];

        if (sortOrder?.length) {
            holdingNames = _.sortBy(holdingNames, (name) =>
                sortOrder.indexOf(name) !== -1 ? sortOrder.indexOf(name) : holdingNames.length
            );
        } else {
            holdingNames.sort();
        }
    }

    return { holdingNames, preparedData, initialLegend };
};

const HoldingsOverTimeChart = (props: commonTypes.WidgetInstanceProps) => {
    const { documentType, colorPalettes } = commonHooks.useAppContext();
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const {
        styles,
        chartTitles,
        options,
        dataKey,
        columns,
        otherText,
        isLimitHoldingsToTen,
        isSortHoldingsTable,
        orderToSortHoldingsTable,
        paletteId,
    } = widgetProperties || {};

    const i18n = commonHooks.useLocalization();

    const { data } = props;
    const otherTextHtml = i18n.format(otherText, data);

    const allocationsData: any = dataKey ? JsonQuery(`${dataKey}`, { data }).value : sampleData;
    const _orderToSortHoldingsTable: string = i18n.format(orderToSortHoldingsTable, data);
    const { preparedData, holdingNames, initialLegend } = getPreparedData(
        allocationsData,
        otherTextHtml,
        isLimitHoldingsToTen,
        isSortHoldingsTable,
        _orderToSortHoldingsTable,
        i18n
    );

    const [legendData, setLegendData] = useState('');
    const [highstock, setHighstock] = useState<any>(null);

    useEffect(() => {
        import('highcharts/highstock').then((highstock) => setHighstock(highstock));
    }, []);

    const holdingsOverTimeChartDataId = uuidv4();
    let orderedColors =
        data?.themeSelected && paletteId
            ? getThemeBasedColorPalette(colorPalettes?.[paletteId], data?.colorSpace)
            : getColorPalette();

    let highChartOptions: Highcharts.Options = {
        chart: {
            type: 'area',
            marginTop: 30,
            events: {
                load(this: any) {
                    setLegendData(() => JSON.stringify(initialLegend));
                },
            },
        },
        title: {
            text: '',
        },
        xAxis: {
            type: 'datetime',
            tickLength: 4,
            tickWidth: 0,
            opposite: true,
            // Enable to show one tick per year (ms * sec * min * hr * day = 1 yr)
            tickInterval: 1000 * 60 * 60 * 24 * 365,
            // Padding options remove whitespace on either side of the data
            maxPadding: 0,
            minPadding: 0,
            minRange: 1000 * 60 * 60 * 24 * 30,
            startOnTick: false,
            endOnTick: false,
            lineColor: '#000',
            labels: {
                style: {
                    fontSize: '7pt',
                    color: '#000',
                },
                format: '{value: %b %Y}',
                allowOverlap: false,
                y: 300,
            },
            crosshair: {
                width: 2,
                color: '#000',
                label: {
                    enabled: true,
                },
            },
        },
        yAxis: {
            min: 0,
            max: 100,
            tickInterval: 20,
            labels: {
                x: 0,
                y: 0,
            },
            opposite: false,
            gridLineWidth: 0,
            showLastLabel: true,
        },
        legend: { enabled: false },
        credits: { enabled: false },
        colors: orderedColors,
        rangeSelector: {
            enabled: false,
        },
        scrollbar: {
            enabled: false,
        },
        navigator: {
            series: {
                fillOpacity: 1,
            },
        },
        plotOptions: {
            area: {
                stacking: 'percent',
                lineColor: '#ffffff',
                lineWidth: 1,
            },
        },
        tooltip: {
            distance: 4,
            style: {
                color: '#000',
            },
            formatter(this: any) {
                const legend: any = [];
                const toolTipHtml = this.points.reduce(function (s: any, point: any) {
                    legend.push({
                        holding: point.series.name,
                        allocation: point.y / 100,
                    });
                    return `${s}<br/>${point.series.name}: ${point.y}%`;
                }, '');

                setLegendData(() => JSON.stringify(legend));
                return toolTipHtml;
            },
        },
        series: preparedData,
    };

    Array.isArray(options) &&
        options.forEach((o) => {
            let userOptions;

            try {
                userOptions = parse((o.value as string) || '{}');
            } catch (error) {
                userOptions = {};
            }

            orderedColors = userOptions?.pie?.colors?.length
                ? userOptions.pie.colors
                : orderedColors;
            highChartOptions = merge(highChartOptions, o.path, userOptions);
        });

    const legend: any =
        Array.isArray(holdingNames) &&
        holdingNames.map((a) => {
            const value = JsonQuery(`[holding=${a}].allocation`, {
                data: legendData ? parse(legendData) : [],
            }).value;
            return { holding: a, allocation: value ? i18n.t(value, 'pctMax2') : '' };
        });

    const isRenderChart = Array.isArray(preparedData) && preparedData.length;

    return (
        <>
            <div
                className={`holdings-overtime-chart ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
                css={css`
                    ${HoldingsOverTimeChartStyles}${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles}
                `}
            >
                <HeaderWrapper headings={chartTitles} data={data} />
                {isRenderChart && (
                    <>
                        <ChartTable
                            columns={columns}
                            colors={JSON.stringify(orderedColors)}
                            data={legend}
                            dataKey={' '}
                        />
                        <div
                            id={`${holdingsOverTimeChartDataId}`}
                            className="js-holdings-overtime-chart-tile"
                        >
                            {['pdf', 'pptx'].includes(documentType) ? (
                                <div
                                    id={`${holdingsOverTimeChartDataId}-holdings-overtime-chart`}
                                />
                            ) : (
                                highstock && (
                                    <HighchartsReact
                                        highcharts={highstock}
                                        options={highChartOptions}
                                        constructorType={`stockChart`}
                                    />
                                )
                            )}
                        </div>
                    </>
                )}
            </div>
            {['pdf', 'pptx'].includes(documentType) && (
                <>
                    <script
                        id={`${holdingsOverTimeChartDataId}-chart-options`}
                        type="text/json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(highChartOptions),
                        }}
                    ></script>
                    <script
                        id={`${holdingsOverTimeChartDataId}-json-data`}
                        type="text/json"
                        dangerouslySetInnerHTML={{ __html: JSON.stringify(preparedData) }}
                    ></script>
                </>
            )}
        </>
    );
};

export default HoldingsOverTimeChart;
